import type { PresentationLayout } from "@logic/lib/design/presentation-layout"

import type { SolarResidence } from "../solar-presentation"

type HomeImage = NonNullable<SolarResidence[`image`]>

/** V1's System Summary fills, drawn over the photo whatever the theme. */
const PANEL_FRAME_FILL = `#ddd8`
const PANEL_CELL_FILL = `#0008`

/** The smallest square of the designer's canvas the hero zooms to. */
const MIN_VIEW_SIDE = 400

/**
 * The top share of the view the panels are framed in. The address text and
 * its gradient hold the bottom of the card, so the panels sit above them on
 * every card shape: when the card is wider than the view the view fills its
 * height, and when narrower the centered view's band ends higher still.
 */
const PANEL_BAND = 0.56

/** The panels, padded, framed in the top band of a view at least MIN_VIEW_SIDE square. */
export const roofViewBox = ({ focus }: PresentationLayout): string => {
  const pad = Math.max(32, 0.15 * Math.max(focus.w, focus.h))
  const width = Math.max(focus.w + 2 * pad, MIN_VIEW_SIDE)
  const height = Math.max((focus.h + 2 * pad) / PANEL_BAND, MIN_VIEW_SIDE)
  const x = focus.x + focus.w / 2 - width / 2
  const y = focus.y + focus.h / 2 - (height * PANEL_BAND) / 2

  return [x, y, width, height].map((value) => value.toFixed(1)).join(` `)
}

const toPoints = (
  polygon: PresentationLayout[`panels`][number][`frame`],
): string => polygon.map(([x, y]) => `${x},${y}`).join(` `)

/** The photo again, blurred, behind whatever the framed image leaves uncovered. */
function BlurredBackdrop({ url }: { url: string }): JSX.Element {
  return (
    <img
      src={url}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      className="absolute inset-0 size-full scale-110 object-cover opacity-60 blur-2xl"
    />
  )
}

/**
 * A Vision design's site image with its drawn panels, framed on the panels.
 * The image is an HTML img inside the SVG so the print waits for it, and a
 * failed load drops the whole drawing for the caller's fallback: panels are
 * never drawn over any other image.
 */
function RoofLayout({
  image,
  layout,
  alt,
  onError,
}: {
  image: HomeImage
  layout: PresentationLayout
  alt: string
  onError: () => void
}): JSX.Element {
  return (
    <>
      <BlurredBackdrop url={image.url} />
      <svg
        data-testid="solar-roof-layout"
        role="img"
        aria-label={alt}
        viewBox={roofViewBox(layout)}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 size-full"
      >
        <foreignObject
          x={layout.image.x}
          y={layout.image.y}
          width={layout.image.w}
          height={layout.image.h}
        >
          <img
            src={image.url}
            onError={onError}
            alt=""
            loading="lazy"
            decoding="async"
            className="block size-full"
          />
        </foreignObject>
        {layout.panels.map((panel, index) => (
          // The layout carries no ids and stacked panels can share corners;
          // the list is fixed for a given read, so its order is the identity.
          <g key={index} data-testid="solar-roof-panel">
            <polygon points={toPoints(panel.frame)} fill={PANEL_FRAME_FILL} />
            <polygon points={toPoints(panel.cell)} fill={PANEL_CELL_FILL} />
          </g>
        ))}
      </svg>
    </>
  )
}

/**
 * The aerial card's picture. A Vision design draws its panels over the site
 * image it was placed on; an Aurora screenshot already shows them, so it is
 * shown whole; any other image fills the card on its own.
 */
export function HomePicture({
  image,
  layout,
  alt,
  onError,
}: {
  image: HomeImage
  layout: PresentationLayout | null
  alt: string
  onError: () => void
}): JSX.Element {
  if (image.kind === `site_imagery` && layout) {
    return (
      <RoofLayout image={image} layout={layout} alt={alt} onError={onError} />
    )
  }

  if (image.kind === `design_screenshot`) {
    return (
      <>
        <BlurredBackdrop url={image.url} />
        <img
          data-testid="solar-design-screenshot"
          src={image.url}
          onError={onError}
          alt={alt}
          width={image.width ?? undefined}
          height={image.height ?? undefined}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 size-full object-contain"
        />
      </>
    )
  }

  return (
    <img
      src={image.url}
      onError={onError}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="absolute inset-0 size-full object-cover"
    />
  )
}
